import { NewsModel, UserModel } from '@/models';
import { auth } from '@/services';

// TODO: remove user parameter in each method in favour of injecting the auth service
// (implemented on the epic/polls-permissions branch)
interface iNewsPolicy {
    /**
     * Whether the user can create new news articles.
     *
     * @param {UserModel=} user
     * @returns {boolean}
     */
    canCreate(user?: UserModel): boolean;

    /**
     * Whether the user can delete a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    canDelete(user?: UserModel, news?: NewsModel): boolean;

    /**
     * Whether the user can manage a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    canManage(user?: UserModel, news?: NewsModel): boolean;

    /**
     * Whether a user can manage the status field of a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    canManageStatus(user?: UserModel, news?: NewsModel): boolean;

    /**
     * Whether the user can manage the created date of a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    canManageCreatedDate(user?: UserModel, news?: NewsModel): boolean;

    /**
     * Whether the user must pay to create a new news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    createPaymentRequired(user?: UserModel, news?: NewsModel): boolean;
}

export class News implements iNewsPolicy {
    /**
     * Whether the user can create new news articles.
     *
     * @param {UserModel=} user
     * @returns {boolean}
     */
    public canCreate(user?: UserModel): boolean {
        if (!user) {
            return false;
        }

        return user.hasPermission('news.create') || user.hasPermission('news.create-billable') || user.hasPermission('news.manage');
    }

    /**
     * Whether the user can delete a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    public canDelete(user?: UserModel, news?: NewsModel): boolean {
        if (!user) {
            return false;
        }

        // users can delete their own posts
        if (news && news.data.user_id === user.data.id) {
            return true;
        }

        return user.hasPermission('news.manage');
    }

    /**
     * Whether the user can manage a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    public canManage(user?: UserModel, news?: NewsModel): boolean {
        if (!user) {
            return false;
        }

        // users can manage their own news articles
        if (news && news.data.user_id === user.data.id) {
            return true;
        }

        return user.hasPermission('news.manage');
    }

    /**
     * Whether the user can manage the created date of a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    public canManageCreatedDate(user?: UserModel, news?: NewsModel): boolean {
        if (!user) {
            return false;
        }


        return user.hasPermission('news.manage');
    }

    /**
     * Whether a user can manage the status field of a news article.
     *
     * @param {UserModel=} user
     * @param {NewsModel=} news
     * @returns {boolean}
     */
    public canManageStatus(user?: UserModel, news?: NewsModel): boolean {
        if (!user) {
            return false;
        }

        return user.hasPermission('news.manage');
    }

    /**
     * Whether the user must pay to create a new news article.
     *
     * @param {UserModel=} user
     * @returns {boolean}
     */
    public createPaymentRequired(user?: UserModel, news?: NewsModel): boolean {
        if (!user) {
            return false;
        }

        return user.hasPermission('news.create-billable');
    }
}

export default new News();
